import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import Breadcrumb from '../components/Breadcrumb'
import KPICard from '../components/KPICard'

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
]

const TYPE_LABELS = {
    'loe':            { title: 'LOE', breadcrumb: 'LOE', breadcrumbPath: '/financials/loe', kpiTitle: 'Total LOE' },
    'loe-only':       { title: 'LOE', breadcrumb: 'LOE', breadcrumbPath: '/financials/loe', kpiTitle: 'Total LOE' },
    'ga':             { title: 'G&A', breadcrumb: 'G&A', breadcrumbPath: '/financials', kpiTitle: 'Total G&A' },
    'workover':       { title: 'Workover', breadcrumb: 'Workover', breadcrumbPath: '/financials', kpiTitle: 'Total Workover' },
    'overhead':       { title: 'Overhead', breadcrumb: 'Overhead', breadcrumbPath: '/financials', kpiTitle: 'Total Overhead' },
    'advalorem':      { title: 'Ad Valorem', breadcrumb: 'Ad Valorem', breadcrumbPath: '/financials', kpiTitle: 'Total Ad Valorem' },
    'capex':          { title: 'CapEx', breadcrumb: 'CapEx', breadcrumbPath: '/financials', kpiTitle: 'Total CapEx' },
    'production-tax': { title: 'Production Taxes', breadcrumb: 'Prod. Tax', breadcrumbPath: '/financials', kpiTitle: 'Total Taxes' },
    'royalties':      { title: 'Royalties', breadcrumb: 'Royalties', breadcrumbPath: '/financials', kpiTitle: 'Total Royalties' },
}

function LOEMonthDetail() {
    const { theme } = useTheme()
    const { yearMonth } = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const isDark = theme === 'dark'

    const type = searchParams.get('type') || 'loe'
    const typeInfo = TYPE_LABELS[type] || TYPE_LABELS['loe']

    // Parse year and month from URL param (e.g., "2025-12")
    const [year, month] = (yearMonth || '').split('-').map(Number)
    const monthName = MONTH_NAMES[month] || ''
    const shortLabel = `${monthName.slice(0, 3)} '${String(year).slice(2)}`

    const [glData, setGlData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    // ?category=X (from a P&L LOE category sub-row) opens that category pre-expanded
    const [expandedCategory, setExpandedCategory] = useState(searchParams.get('category'))

    useEffect(() => {
        if (!year || !month) return
        fetchAllGL()
    }, [year, month, type])

    const fetchAllGL = async () => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams({ year, month, type })
            const res = await fetch(`${apiConfig.baseUrl}/financials/loe-gl?${params}`)
            if (!res.ok) throw new Error('Failed to fetch GL transactions')
            const data = await res.json()
            setGlData(data)
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    // Group transactions by category
    const categoryBreakdown = React.useMemo(() => {
        if (!glData?.transactions) return []
        const map = {}
        glData.transactions.forEach(tx => {
            const cat = tx.category || 'Uncategorized'
            if (!map[cat]) map[cat] = { category: cat, actual: 0, transactions: [] }
            map[cat].actual += tx.amount || 0
            map[cat].transactions.push(tx)
        })
        return Object.values(map).sort((a, b) => b.actual - a.actual)
    }, [glData])

    const totalAmount = glData?.total || 0
    const transactionCount = glData?.transactionCount || 0

    const breadcrumbs = [
        { label: 'Financials', path: '/financials' },
        { label: typeInfo.breadcrumb, path: typeInfo.breadcrumbPath },
        { label: shortLabel }
    ]

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <Breadcrumb items={breadcrumbs} />

            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                    <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                    {typeInfo.title}: {monthName} {year}
                </h1>
                <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                    {glData?.typeLabel || typeInfo.title} breakdown and invoice detail
                </p>
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p>{error}</p>
                </div>
            )}

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <KPICard title={typeInfo.kpiTitle} value={`$${Math.round(Math.abs(totalAmount)).toLocaleString()}`} />
                <KPICard title="Transactions" value={transactionCount.toLocaleString()} />
                <KPICard title="Categories" value={categoryBreakdown.length} />
                <KPICard title="Avg per Category"
                    value={`$${Math.round(totalAmount / Math.max(categoryBreakdown.length, 1)).toLocaleString()}`} />
            </div>

            {/* Category Breakdown with GL Drill-Down */}
            <div className="chart-container">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                    Categories
                    <span className={`text-xs font-normal ml-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Click to see invoices
                    </span>
                </h2>
                <div className="overflow-x-auto">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th className="w-8"></th>
                                <th>Category</th>
                                <th className="text-right">Amount</th>
                                <th className="text-right w-16">%</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryBreakdown.map((catRow, i) => {
                                const isExpanded = expandedCategory === catRow.category
                                const pct = totalAmount ? ((catRow.actual / totalAmount) * 100).toFixed(1) : '0'
                                return (
                                    <React.Fragment key={catRow.category}>
                                        <tr className={`cursor-pointer ${isExpanded ? (isDark ? 'bg-slate-700/40' : 'bg-blue-50') : 'hover:bg-slate-700/20'}`}
                                            onClick={() => setExpandedCategory(isExpanded ? null : catRow.category)}>
                                            <td className="w-8 text-center">
                                                <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                                    &#9654;
                                                </span>
                                            </td>
                                            <td className="font-medium">{catRow.category}</td>
                                            <td className="text-right font-mono">${Math.round(catRow.actual).toLocaleString()}</td>
                                            <td className="text-right text-sm">{pct}%</td>
                                        </tr>

                                        {isExpanded && (
                                            <tr>
                                                <td colSpan={4} className="p-0">
                                                    <div className={`${isDark ? 'bg-slate-800/60' : 'bg-gray-50'} pl-8 pr-4 py-3`}>
                                                        <div className="flex justify-between items-center mb-2">
                                                            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                {catRow.transactions.length} transactions | ${Math.round(catRow.actual).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                                            <table className={`w-full text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                <thead className={`sticky top-0 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                                                    <tr>
                                                                        <th className="text-left px-2 py-1.5 font-medium">Vendor</th>
                                                                        <th className="text-left px-2 py-1.5 font-medium">Well</th>
                                                                        <th className="text-left px-2 py-1.5 font-medium">Description</th>
                                                                        <th className="text-left px-2 py-1.5 font-medium">Invoice #</th>
                                                                        <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {catRow.transactions.map((tx, j) => (
                                                                        <tr key={j} className={`${isDark ? 'border-t border-slate-600/30 hover:bg-slate-700/40' : 'border-t border-gray-200 hover:bg-gray-100'}`}>
                                                                            <td className="px-2 py-1 whitespace-nowrap">{tx.vendorName || '--'}</td>
                                                                            <td className="px-2 py-1 whitespace-nowrap">{tx.wellName}</td>
                                                                            <td className="px-2 py-1 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                                                                            <td className="px-2 py-1 whitespace-nowrap">
                                                                                {tx.docVueId ? (
                                                                                    <a href={`https://aviatorenergy.docvuecloud.com/document/${tx.docVueId}?from=%2Frepository`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline" title={`DocVue: ${tx.docVueId}`}>
                                                                                        {tx.invoiceNum || tx.docVueId}
                                                                                    </a>
                                                                                ) : (tx.invoiceNum || '--')}
                                                                            </td>
                                                                            <td className="px-2 py-1 text-right font-mono whitespace-nowrap">
                                                                                ${tx.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                            <tr className={`font-bold ${isDark ? 'border-t-2 border-slate-500' : 'border-t-2 border-gray-400'}`}>
                                <td></td>
                                <td>Total</td>
                                <td className="text-right font-mono">${Math.round(totalAmount).toLocaleString()}</td>
                                <td className="text-right">100%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default LOEMonthDetail
